import { Terminal } from "@xterm/xterm";

import "@xterm/xterm/css/xterm.css";

/**
 * The terminal the device pane writes into, and the two things xterm.js does
 * not do for itself here.
 *
 * One is size. xterm is told a number of columns and rows, not a box, so
 * something has to divide the box by the size of a character; the addon that
 * usually does it reaches into xterm's private core for the cell size, and this
 * measures a string in the terminal's own font instead — the same way xterm
 * measures it internally, a run of 32 characters divided by 32, which is
 * exact for a monospace font and costs one layout per resize.
 *
 * The other is color. xterm draws with colors it is given, not the
 * stylesheet's, so it is given the computed background and text color of the
 * element it is in, and given them again when the page's theme changes —
 * which theme.ts announces on the root element's `data-theme`, the attribute
 * the stylesheet keys on.
 */
export interface PaneTerminal {
    write(bytes: Uint8Array | string): void;
    clear(): void;
    dispose(): void;
}

/**
 * A stack of the monospace fonts a machine is likely to have, and nothing to
 * download: a webfont arriving late would change the cell size after the first
 * fit, and the pane would be a column out until the next resize.
 */
const FONT_FAMILY = '"SF Mono", "Cascadia Mono", "DejaVu Sans Mono", Menlo, Consolas, "Liberation Mono", monospace';
const FONT_SIZE = 12;
const LINE_HEIGHT = 1.2;

export function createTerminal(root: HTMLElement): PaneTerminal {
    const term = new Terminal({
        // A serial monitor is not a shell: nothing typed here goes anywhere,
        // and ESP-IDF's own output is the only thing on screen.
        disableStdin: true,
        cursorBlink: false,
        cursorStyle: "bar",
        // An app built to end its lines with a bare "\n" still starts each
        // line at the left. Translating it leaves a "\r\n" alone.
        convertEol: true,
        scrollback: 20000,
        fontFamily: FONT_FAMILY,
        fontSize: FONT_SIZE,
        lineHeight: LINE_HEIGHT,
        theme: paneColors(root),
    });
    term.open(root);

    let cell: { width: number; height: number } | undefined;
    const fit = (): void => {
        if (cell === undefined) cell = measureCell(root);
        if (cell === undefined) return;
        // `clientWidth` counts the padding of the element the terminal is in,
        // and xterm draws inside it, so whatever the page styles the host with
        // comes off here rather than being left to wrap a column early.
        const style = getComputedStyle(root);
        const width = root.clientWidth - pixels(style.paddingLeft) - pixels(style.paddingRight);
        const height = root.clientHeight - pixels(style.paddingTop) - pixels(style.paddingBottom);
        if (width <= 0 || height <= 0) return;
        const cols = Math.max(2, Math.floor(width / cell.width));
        const rows = Math.max(1, Math.floor(height / cell.height));
        if (cols !== term.cols || rows !== term.rows) term.resize(cols, rows);
    };

    // A pane that is not on screen yet measures as zero, and `measureCell`
    // refuses to cache that, so the first observation after it is laid out is
    // what settles the size.
    const observer = new ResizeObserver(() => fit());
    observer.observe(root);
    fit();

    const themeObserver = new MutationObserver(() => {
        term.options.theme = paneColors(root);
    });
    themeObserver.observe(document.documentElement, { attributeFilter: ["data-theme"] });

    return {
        write(bytes: Uint8Array | string): void {
            term.write(bytes);
        },
        clear(): void {
            term.clear();
        },
        dispose(): void {
            observer.disconnect();
            themeObserver.disconnect();
            term.dispose();
        },
    };
}

/** The host's colors as the stylesheet resolved them for the current theme. */
function paneColors(root: HTMLElement): { background: string; foreground: string; cursor: string } {
    const style = getComputedStyle(root);
    return { background: style.backgroundColor, foreground: style.color, cursor: style.color };
}

/**
 * The size of one cell, from a run of characters in the terminal's own font.
 *
 * Thirty-two of them because one character's box is rounded to a whole pixel
 * and the error would then be a column or two across a wide pane. The height
 * is rounded up the way xterm rounds the row it draws, so the rows counted
 * here are the rows it will lay out.
 */
function measureCell(root: HTMLElement): { width: number; height: number } | undefined {
    const probe = document.createElement("span");
    probe.style.position = "absolute";
    probe.style.visibility = "hidden";
    probe.style.whiteSpace = "pre";
    probe.style.fontFamily = FONT_FAMILY;
    probe.style.fontSize = `${FONT_SIZE}px`;
    probe.textContent = "W".repeat(32);
    root.appendChild(probe);
    const box = probe.getBoundingClientRect();
    probe.remove();
    if (box.width === 0 || box.height === 0) return undefined;
    return { width: box.width / 32, height: Math.ceil(box.height * LINE_HEIGHT) };
}

function pixels(length: string): number {
    const value = Number.parseFloat(length);
    return Number.isFinite(value) ? value : 0;
}
